import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import RateReviewRoundedIcon from '@mui/icons-material/RateReviewRounded';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';

import AppNavbar from '../components/AppNavbar';
import Header from '../components/Header';
import SideMenu from '../components/SideMenu';
import AppTheme from '../shared-theme/AppTheme';
import axiosInstance from '../utils/axiosInstance';
import { formatCapabilityLabel } from '../utils/capabilityContract';

import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from './theme/customizations';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

const REQUESTS_LAST_VIEWED_KEY = 'amx.requests.lastViewedAt';
const WORKSPACE_AVAILABLE_STATUSES = new Set([
  'accepted',
  'in_progress',
  'waiting',
  'awaiting_resource',
  'completed',
  'failed',
]);

function statusLabel(value) {
  if (value === 'pending_provider_response') {
    return 'Awaiting Response';
  }
  if (value === 'open_for_proposals') {
    return 'Open for Proposals';
  }
  if (value === 'proposal_submitted') {
    return 'Proposal Submitted';
  }
  if (value === 'proposal_selected') {
    return 'Proposal Selected';
  }
  if (value === 'in_fulfillment') {
    return 'In Fulfillment';
  }
  return formatCapabilityLabel(value || 'unknown');
}

function statusColor(value) {
  if (['accepted', 'active', 'proposal_selected', 'in_fulfillment', 'completed'].includes(value)) {
    return 'success';
  }
  if (['declined', 'cancelled'].includes(value)) {
    return 'error';
  }
  return 'warning';
}

function formatTimestamp(value) {
  if (!value) {
    return 'N/A';
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
}

function businessUnitLabel(unit) {
  const parent = unit?.parent_partner?.display_name || unit?.parent_partner?.partner_code;
  const unitName = unit?.display_name || unit?.business_unit_code || unit?.business_unit_id;
  return parent ? `${parent} / ${unitName}` : unitName;
}

function EmptyRows({ view }) {
  return (
    <TableRow>
      <TableCell colSpan={6}>
        <Typography variant="body2" color="text.secondary">
          {view === 'incoming'
            ? 'No incoming requests.'
            : view === 'opportunities'
              ? 'No need-based opportunities for your provider Business Units.'
              : 'No sent requests.'}
        </Typography>
      </TableCell>
    </TableRow>
  );
}

function SentRequestRow({ row }) {
  const capabilities = Array.isArray(row.capabilities) ? row.capabilities : [];
  const isNeedBased = row.request_type === 'need_based';
  return (
    <TableRow key={row.request_id} hover>
      <TableCell sx={{ minWidth: 240 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {row.title || 'Untitled Request'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {businessUnitLabel(row.requesting_business_unit)}
        </Typography>
      </TableCell>
      <TableCell>{businessUnitLabel(row.requesting_business_unit)}</TableCell>
      <TableCell sx={{ minWidth: 260 }}>
        <Stack spacing={0.75}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {isNeedBased && !capabilities.length
              ? (row.need_category?.category_display_name || row.need_category?.category_name || 'Need-Based Request')
              : `${row.capability_count || capabilities.length} Capabilit${(row.capability_count || capabilities.length) === 1 ? 'y' : 'ies'}`}
          </Typography>
          {isNeedBased ? <Chip size="small" color="primary" variant="outlined" label={`${row.proposal_count || 0} Proposal${row.proposal_count === 1 ? '' : 's'}`} /> : null}
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {capabilities.slice(0, 4).map((capability) => (
              <Chip
                key={capability.request_capability_id}
                size="small"
                variant="outlined"
                color={statusColor(capability.status)}
                label={`${capability.capability_name || 'Capability'}: ${statusLabel(capability.status)}`}
              />
            ))}
            {capabilities.length > 4 ? <Chip size="small" label={`+${capabilities.length - 4}`} /> : null}
          </Stack>
        </Stack>
      </TableCell>
      <TableCell>
        <Chip size="small" color={statusColor(row.status)} label={statusLabel(row.status)} />
      </TableCell>
      <TableCell>{formatTimestamp(row.created_at)}</TableCell>
      <TableCell align="right">
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            component={RouterLink}
            to={`/requests/${row.request_id}`}
            size="small"
            variant="contained"
            startIcon={<RateReviewRoundedIcon />}
          >
            Open Request
          </Button>
          {row.workspace_id ? (
            <Button
              component={RouterLink}
              to={`/workspaces/${row.workspace_id}`}
              size="small"
              startIcon={<OpenInNewRoundedIcon />}
            >
              Workspace
            </Button>
          ) : null}
        </Stack>
      </TableCell>
    </TableRow>
  );
}

function NeedOpportunityRow({ row }) {
  return (
    <TableRow key={row.request_id} hover>
      <TableCell sx={{ minWidth: 240 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>{row.title || 'Untitled Need'}</Typography>
        <Typography variant="caption" color="text.secondary">{businessUnitLabel(row.requesting_business_unit)}</Typography>
      </TableCell>
      <TableCell>{row.need_category?.category_display_name || row.need_category?.category_name || 'Need Category'}</TableCell>
      <TableCell>{businessUnitLabel(row.requesting_business_unit)}</TableCell>
      <TableCell><Chip size="small" color={statusColor(row.status)} label={statusLabel(row.status)} /></TableCell>
      <TableCell>{formatTimestamp(row.created_at)}</TableCell>
      <TableCell align="right">
        <Button
          component={RouterLink}
          to={`/requests/need-opportunities/${row.request_id}`}
          size="small"
          variant="contained"
          startIcon={<RateReviewRoundedIcon />}
        >
          Review Opportunity
        </Button>
      </TableCell>
    </TableRow>
  );
}

function IncomingRequestRow({ row }) {
  return (
    <TableRow key={row.request_capability_id} hover>
      <TableCell sx={{ minWidth: 220 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {row.title || 'Untitled Request'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {businessUnitLabel(row.requesting_business_unit)}
        </Typography>
      </TableCell>
      <TableCell>{row.capability_name || 'Capability'}</TableCell>
      <TableCell>{businessUnitLabel(row.requesting_business_unit)}</TableCell>
      <TableCell>
        <Chip size="small" color={statusColor(row.status)} label={statusLabel(row.status)} />
      </TableCell>
      <TableCell>{formatTimestamp(row.created_at)}</TableCell>
      <TableCell align="right">
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            component={RouterLink}
            to={`/requests/direct/${row.request_capability_id}`}
            size="small"
            variant={row.status === 'pending_provider_response' ? 'contained' : 'outlined'}
            startIcon={<RateReviewRoundedIcon />}
          >
            {row.status === 'pending_provider_response' ? 'Review' : 'View'}
          </Button>
          {row.workspace_id && WORKSPACE_AVAILABLE_STATUSES.has(row.status) ? (
            <Button
              component={RouterLink}
              to={`/workspaces/${row.workspace_id}`}
              size="small"
              startIcon={<OpenInNewRoundedIcon />}
            >
              Workspace
            </Button>
          ) : null}
        </Stack>
      </TableCell>
    </TableRow>
  );
}

export default function RequestsStubPage(props) {
  const location = useLocation();
  const [view, setView] = useState(
    ['sent', 'incoming', 'opportunities'].includes(location.state?.view)
      ? location.state.view
      : 'sent',
  );
  const [loading, setLoading] = useState(true);
  const [rowsByView, setRowsByView] = useState({ sent: [], incoming: [], opportunities: [] });
  const [notifications, setNotifications] = useState([]);
  const [lastViewedAt, setLastViewedAt] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let isMounted = true;
    const previousViewedAt = window.localStorage.getItem(REQUESTS_LAST_VIEWED_KEY) || '';
    setLastViewedAt(previousViewedAt);
    window.localStorage.setItem(REQUESTS_LAST_VIEWED_KEY, new Date().toISOString());

    async function loadNotifications() {
      try {
        const response = await axiosInstance.get('/notifications', { params: { limit: 100 } });
        if (isMounted) {
          setNotifications(Array.isArray(response.data?.notifications) ? response.data.notifications : []);
        }
      } catch (notificationError) {
        if (notificationError?.response?.status !== 401 && notificationError?.response?.status !== 403) {
          console.error('Failed to load request notifications', notificationError);
        }
      }
    }

    async function loadRequests() {
      setLoading(true);
      setError('');
      try {
        const [sentResponse, incomingResponse, opportunitiesResponse] = await Promise.all([
          axiosInstance.get('/requests', { params: { view: 'sent' } }),
          axiosInstance.get('/requests/direct', { params: { view: 'incoming' } }),
          axiosInstance.get('/requests/need-opportunities', { params: { view: 'opportunities' } }),
        ]);
        if (!isMounted) {
          return;
        }
        setRowsByView({
          sent: Array.isArray(sentResponse.data?.requests) ? sentResponse.data.requests : [],
          incoming: Array.isArray(incomingResponse.data?.requests) ? incomingResponse.data.requests : [],
          opportunities: Array.isArray(opportunitiesResponse.data?.requests) ? opportunitiesResponse.data.requests : [],
        });
        await loadNotifications();
      } catch (err) {
        if (!isMounted) {
          return;
        }
        setRowsByView({ sent: [], incoming: [], opportunities: [] });
        setError(err?.response?.data?.message || err?.message || 'Unable to load requests.');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadRequests();
    const notificationTimer = window.setInterval(loadNotifications, 60000);
    return () => {
      isMounted = false;
      window.clearInterval(notificationTimer);
    };
  }, []);

  const rows = rowsByView[view] || [];
  const notificationCounts = useMemo(() => {
    const viewedTimestamp = Date.parse(lastViewedAt);
    if (Number.isNaN(viewedTimestamp)) {
      return { sent: 0, incoming: 0, opportunities: 0 };
    }

    const requestKeys = {
      sent: new Set(),
      incoming: new Set(),
      opportunities: new Set(),
    };

    notifications.forEach((notification) => {
      const createdTimestamp = Date.parse(notification.created_at);
      const metadata = notification.metadata || {};
      const requestId = metadata.request_id;
      if (!requestId || Number.isNaN(createdTimestamp) || createdTimestamp <= viewedTimestamp) {
        return;
      }

      if (notification.event_type === 'request.updated') {
        requestKeys.sent.add(requestId);
      } else if (notification.event_type === 'request.incoming') {
        requestKeys[metadata.request_type === 'need_based' ? 'opportunities' : 'incoming'].add(requestId);
      }
    });

    return {
      sent: requestKeys.sent.size,
      incoming: requestKeys.incoming.size,
      opportunities: requestKeys.opportunities.size,
    };
  }, [lastViewedAt, notifications]);

  const tabLabel = (label, count) => (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <span>{label}</span>
      {count > 0 ? <Chip size="small" color="primary" label={count} sx={{ height: 20, minWidth: 20, '& .MuiChip-label': { px: 0.75 } }} /> : null}
    </Stack>
  );

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <AppNavbar />
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <Header
            breadcrumbItems={[
              { label: 'AMX xConsole', href: '/' },
              { label: 'Requests' },
            ]}
          />
          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 800 }}>
                  Requests
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Capability requests sent by or addressed to your Business Units.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button component={RouterLink} to="/requests/new" variant="contained" startIcon={<AddRoundedIcon />}>
                  Create Request
                </Button>
                <Button component={RouterLink} to="/requests/discover" variant="contained">
                  Discover Capabilities
                </Button>
                <Button component={RouterLink} to="/browse-capabilities" variant="outlined">
                  Browse Capabilities
                </Button>
              </Stack>
            </Stack>

            {error ? <Alert severity="error">{error}</Alert> : null}

            <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
              <Tabs value={view} onChange={(_event, value) => setView(value)}>
                <Tab value="sent" label={tabLabel('Sent', notificationCounts.sent)} />
                <Tab value="incoming" label={tabLabel('Incoming', notificationCounts.incoming)} />
                <Tab value="opportunities" label={tabLabel('Need-Based Opportunities', notificationCounts.opportunities)} />
              </Tabs>
              {loading ? (
                <Stack direction="row" spacing={2} alignItems="center" sx={{ p: 2 }}>
                  <CircularProgress size={22} />
                  <Typography>Loading requests...</Typography>
                </Stack>
              ) : (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Request</TableCell>
                      <TableCell>{view === 'incoming' ? 'Capability' : view === 'opportunities' ? 'Need Category' : 'Requesting BU'}</TableCell>
                      <TableCell>{view === 'incoming' ? 'Requested By' : view === 'opportunities' ? 'Requester' : 'Capabilities / Need'}</TableCell>
                      <TableCell>{view === 'sent' ? 'Overall' : 'Status'}</TableCell>
                      <TableCell>Submitted</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.length ? rows.map((row) => (
                      view === 'sent'
                        ? <SentRequestRow key={row.request_id} row={row} />
                        : view === 'opportunities'
                          ? <NeedOpportunityRow key={row.request_id} row={row} />
                          : <IncomingRequestRow key={row.request_capability_id} row={row} />
                    )) : <EmptyRows view={view} />}
                  </TableBody>
                </Table>
              )}
            </Paper>
          </Stack>
        </Box>
      </Box>
    </AppTheme>
  );
}
